import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { env } from '@/lib/env'
import type { RouteStop } from './geo'
import type { DrawnRoute } from './routing'
import styles from './RouteMapDialog.module.css'

interface RouteMapProps {
  pickup: RouteStop
  dropoff: RouteStop
  /** The line to draw, or null while routing is still in flight. */
  route: DrawnRoute | null
}

/** Central Cairo — held only for the instant before the real points are fitted. */
const INITIAL_VIEW: L.LatLngTuple = [30.0444, 31.2357]

const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'

/**
 * The OpenStreetMap canvas: two pins and the road between them.
 *
 * Leaflet is driven imperatively rather than through a React wrapper — the map
 * is one long-lived object that must be created against a real element and torn
 * down explicitly, which is a ref and two effects, not a component tree.
 */
export function RouteMap({ pickup, dropoff, route }: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const overlayRef = useRef<L.LayerGroup | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const map = L.map(container, { zoomControl: true, attributionControl: true })
    L.tileLayer(env.mapTileUrl, { maxZoom: 19, attribution: OSM_ATTRIBUTION }).addTo(map)

    // Leaflet refuses to draw until it has a view; the draw effect below
    // replaces this with the booking's own bounds on the same tick.
    map.setView(INITIAL_VIEW, 11)

    mapRef.current = map
    overlayRef.current = L.layerGroup().addTo(map)

    // The dialog animates into place, so the container Leaflet measured at
    // mount is not the one the operator ends up looking at — without this the
    // map renders into a stale size and the tiles sit in the top-left corner.
    const frame = window.requestAnimationFrame(() => map.invalidateSize())
    const afterAnimation = window.setTimeout(() => map.invalidateSize(), 300)

    // The panel is also resized by the browser window and by the mobile
    // keyboard; the observer covers everything the two timers above do not.
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => map.invalidateSize())
        : null
    observer?.observe(container)

    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(afterAnimation)
      observer?.disconnect()
      map.remove()
      mapRef.current = null
      overlayRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const overlay = overlayRef.current
    if (!map || !overlay) return

    overlay.clearLayers()

    const inView: L.LatLngTuple[] = []

    if (pickup.point) {
      stopMarker(pickup.point, 'pickup', 'Pickup', pickup.address).addTo(overlay)
      inView.push([pickup.point.lat, pickup.point.lng])
    }

    if (dropoff.point) {
      stopMarker(dropoff.point, 'dropoff', 'Drop-off', dropoff.address).addTo(overlay)
      inView.push([dropoff.point.lat, dropoff.point.lng])
    }

    if (route && route.path.length > 1) {
      const line: L.LatLngTuple[] = route.path.map((point) => [point.lat, point.lng])
      L.polyline(line, {
        // Styled from the class, not from `color`, so the route keeps using the
        // brand token — Leaflet writes `stroke` as a presentation attribute,
        // which the stylesheet outranks.
        className: route.kind === 'road' ? styles.routeLine : styles.routeLineStraight,
        weight: 5,
        opacity: 0.9,
        lineJoin: 'round',
        lineCap: 'round',
        // A guess at the roads would be a lie; the crow-flies line is dashed so
        // it reads as one at a glance.
        dashArray: route.kind === 'straight' ? '2 12' : undefined,
      }).addTo(overlay)
      inView.push(...line)
    }

    if (inView.length === 0) return
    if (inView.length === 1) {
      map.setView(inView[0], 15)
      return
    }

    map.fitBounds(L.latLngBounds(inView), { padding: [40, 40], maxZoom: 16 })
  }, [pickup, dropoff, route])

  return (
    <div
      ref={containerRef}
      className={styles.canvas}
      role="application"
      aria-label="Route between pickup and drop-off"
    />
  )
}

function stopMarker(
  point: { lat: number; lng: number },
  kind: 'pickup' | 'dropoff',
  label: string,
  address: string | null,
): L.Marker {
  const marker = L.marker([point.lat, point.lng], {
    icon: L.divIcon({
      // Leaflet's own `leaflet-div-icon` draws a white box behind the content;
      // an empty class leaves just the pin.
      className: '',
      html: `<span class="${styles.pin} ${kind === 'pickup' ? styles.pinPickup : styles.pinDropoff}"></span>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    }),
    title: `${label}: ${address ?? 'address not recorded'}`,
    keyboard: false,
  })

  // Built as elements rather than a string: Leaflet sets string tooltip content
  // as HTML, and these addresses are typed by customers.
  const tooltip = document.createElement('div')
  tooltip.className = styles.tooltip

  const heading = document.createElement('strong')
  heading.textContent = label

  const detail = document.createElement('span')
  detail.textContent = address ?? 'Address not recorded'

  tooltip.append(heading, detail)
  marker.bindTooltip(tooltip, { direction: 'top', offset: [0, -12], opacity: 1 })

  return marker
}
